import React, { useCallback } from 'react';
import { Descriptions, message, Skeleton } from 'antd';
import { useRole } from 'common/hooks/useRole';
import {
  BusinessPartnerForm,
  BusinessPartnerFormModel,
} from 'pages/BusinessPartnerManagement/BusinessPartnerForm';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { FormikOnSubmit } from 'lib/formik.utils';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { BusinessPartnerIntegrationCreateButton } from 'pages/SuperAdminModule/BusinessPartnersManagement/BusinessPartnersListPage/BusinessPartnerIntegrationCreateButton';

export const BusinessPartnerPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { isSuperAdmin } = useRole();
  const queryCache = useQueryCache();

  const { isLoading, data } = useQuery(
    [QueryKey.BusinessPartner, bpContext.id],
    () => {
      return BusinessPartnersAPI.fetchById(bpContext.id);
    }
  );

  const [mutate, { isLoading: isSubmitting }] = useMutation(
    BusinessPartnersAPI.update
  );

  const intlMessage = useFormatMessage();

  const handleSubmit: FormikOnSubmit<BusinessPartnerFormModel> = useCallback(
    (formData) => {
      mutate(
        { id: bpContext.id, ...formData },
        {
          onSuccess: (data) => {
            queryCache.invalidateQueries(QueryKey.BusinessPartnersList).then();
            queryCache.setQueryData(
              [QueryKey.BusinessPartner, bpContext.id],
              data
            );
            message.success(
              intlMessage(
                { id: 'businessPartner.updated' },
                { name: formData.name }
              )
            );
          },
        }
      ).then();
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bpContext, queryCache, mutate]
  );

  return (
    <>
      {isLoading && <Skeleton />}
      {!isLoading && (
        <>
          {isSuperAdmin && (
            <BusinessPartnerIntegrationCreateButton
              businessPartner={bpContext}
            />
          )}
          <BusinessPartnerForm
            onSubmit={handleSubmit}
            loading={isSubmitting}
            value={data}
            readonly={true}
          />
          <Descriptions>
            <Descriptions.Item label={intlMessage({ id: 'version' })}>
              {process.env.VERSION}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </>
  );
};
